import { useState } from 'react'
import { SectionHeader, Panel, ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { Forbidden, ErrorStrip, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { EMPTY_UNIT_FILTERS, listUnits } from '@/features/geography/api'
import { explain } from '@/shared/api/problem'
import { applyProposal, proposeCells, type CellProposal } from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'

/**
 * Cell builder — proposal compute → preview → apply.
 */
export function BuilderPage() {
  const canBuild = useCan('turnout:manage-cells')
  const selection = useElection()
  const electionId = selection.electionId
  const [stationId, setStationId] = useState<string>('')
  const [targetSize, setTargetSize] = useState<number>(10)
  const [computing, setComputing] = useState(false)
  const [applying, setApplying] = useState(false)
  const [proposal, setProposal] = useState<CellProposal | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Stations come from `geography`, which owns them — there is no /turnout/stations
  // route and there should not be one; a polling station is a geography_unit.
  const stations = useLoad(
    () => listUnits({ ...EMPTY_UNIT_FILTERS, type: ['POLLING_STATION'] }, 0, 200),
    [])

  if (!canBuild) return <Forbidden what="the Cell builder" />

  /*
     `proposal` and `warnings` arrive as JSON STRINGS — the column is jsonb and the DTO
     carries it verbatim rather than re-modelling the engine's output. Parsed here, and
     defensively: a malformed payload must not blank the whole screen, because the
     numbers beside it (input voters, proposed cells, unassignable) are still true.
  */
  const parse = <T,>(raw: string | null | undefined, fallback: T): T => {
    if (!raw) return fallback
    try { return JSON.parse(raw) as T } catch { return fallback }
  }
  const clusters = parse<Array<{ code: string; voterIds: string[] }>>(proposal?.proposal, [])
  const warnings = parse<string[]>(proposal?.warnings, [])

  const compute = async () => {
    setComputing(true)
    setError(null)
    try {
      if (!electionId || !stationId) return
      setProposal(await proposeCells({ electionId, stationId, targetSize }))
    } catch (e) {
      // The server's own sentence, not "HTTP 409". A budget cap, a stale proposal and
      // a missing authority all read differently and all matter.
      setError(explain(e))
    } finally {
      setComputing(false)
    }
  }

  const apply = async () => {
    if (!proposal) return
    setApplying(true)
    setError(null)
    try {
      await applyProposal(proposal.id)
      setProposal(null) // Clear on success
    } catch (e) {
      setError(explain(e))
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Cell builder"
        description="Run a proposal first. A coordinator proposal is dry-run only — nothing is written until you apply."
        right={<ElectionPicker selection={selection} />}
      />

      <ErrorStrip message={error ?? selection.error ?? stations.error} />

      {warnings.length > 0 && (
        /* The engine says when it fell back to alphabetical grouping. A cell whose
           members cannot reach each other is a cell transport coordination is a
           fiction for, so this is printed, never swallowed. */
        <Note tone="bad">
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </Note>
      )}

      {!proposal && (
        <Panel title="Proposal parameters" description="Pick an election and a station to begin.">
          <div className="p-4 space-y-4 max-w-xl">
            <div>
              <label className="block text-erp-sm font-medium mb-1">Station</label>
              <select value={stationId} onChange={(e) => setStationId(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors">
                <option value="">Pick a station…</option>
                {(stations.data?.content ?? []).map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-erp-sm font-medium mb-1">Target cell size</label>
              <input
                type="number"
                value={targetSize}
                onChange={(e) => setTargetSize(Number(e.target.value))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
              />
            </div>
            <button
              onClick={compute}
              disabled={computing || !electionId || !stationId}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 disabled:opacity-50"
            >
              {computing ? 'Computing...' : 'Compute proposal'}
            </button>
          </div>
        </Panel>
      )}

      {proposal && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Proposal {String(proposal.id).slice(0, 8)}</h2>
            <div className="space-x-3">
              <button
                onClick={() => setProposal(null)}
                disabled={applying}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 disabled:opacity-50"
              >
                Discard
              </button>
              <button
                onClick={apply}
                disabled={applying}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 disabled:opacity-50"
              >
                {applying ? 'Applying...' : 'Apply cells'}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1 space-y-4">
              <Panel title="Summary">
                <div className="p-4 text-erp-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total members</span>
                    <span className="font-medium">{proposal.inputVoters as number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Proposed cells</span>
                    <span className="font-medium">{proposal.proposedCells as number}</span>
                  </div>
                </div>
              </Panel>
              
              <Panel title="Proposed cells"
                description="Nothing here exists yet. Applying writes them; discarding leaves no trace.">
                <ErpTable head={<><Th>Code</Th><Th className="text-right">Members</Th></>}>
                  {clusters.length === 0 ? (
                    <EmptyRow colSpan={2}
                      message="The engine proposed no cells for this station — every voter is already in one, or none could be clustered." />
                  ) : clusters.map(c => (
                    <tr key={c.code} className="hover:bg-muted/40">
                      <Td className="font-mono text-erp-xs">{c.code}</Td>
                      <Td className="text-right tabular-nums">{c.voterIds.length}</Td>
                    </tr>
                  ))}
                </ErpTable>
              </Panel>
            </div>
            
            <div className="col-span-2">
              <Panel title="Map preview" description="Drag members between clusters to adjust.">
                <div className="aspect-video bg-muted/20 flex flex-col items-center justify-center border-b p-4">
                  <div className="text-center space-y-2 text-muted-foreground">
                    <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">Map</div>
                    <p>Map component renders here.</p>
                    <p className="text-xs max-w-xs mx-auto">
                      Points colored by assigned cluster. Unassigned households in grey. Drag lasso to reassign.
                    </p>
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
